/**
 * Week-strip geometry for the Staff Overview "By Machine Group" view.
 *
 * Everything is in wall-clock minutes since Sunday 00:00 of the displayed week
 * (0..10080), never Date objects, so a daylight-saving change cannot shift bars.
 * Gaps are derived here rather than from the API's `unattended` list because
 * that list compares "HH:MM" strings and mishandles shifts that run past
 * midnight.
 */

export const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const DAY_MIN = 24 * 60;
export const WEEK_MIN = 7 * DAY_MIN;

export interface CoverageItem {
  staff: string;
  staff_id: number;
  profile?: string;
  start: string;
  end: string;
  machines_count: number;
  total_group_machines: number;
  on_vacation: boolean;
}

export interface GroupDays {
  [day: string]: { coverage?: CoverageItem[] } | undefined;
}

export interface ShiftBar {
  key: string;
  staffId: number;
  staff: string;
  dayIndex: number;
  startMin: number;
  endMin: number;
  /** Start and end before clipping to the week, for tooltips and durations. */
  rawStartMin: number;
  rawEndMin: number;
  /** A previous-week Saturday shift that runs into this Sunday. */
  carriedOver: boolean;
  startLabel: string;
  endLabel: string;
  machinesCount: number;
  totalMachines: number;
  onVacation: boolean;
  lane: number;
}

export interface GapSegment {
  startMin: number;
  endMin: number;
  kind: "none" | "partial";
  /**
   * Approximate machines covered: the on-shift people's machine counts summed
   * and capped at the group size. The API does not say which machines each
   * person covers, so two people on the same machines are over-counted.
   */
  coveredMachines: number;
}

export interface OverlapSegment {
  startMin: number;
  endMin: number;
  count: number;
}

export function parseHHMM(value: string | null | undefined): number | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(value || "");
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 24 || m > 59) return null;
  return h * 60 + m;
}

function toBar(
  c: CoverageItem,
  key: string,
  dayIndex: number,
  rawStartMin: number,
  rawEndMin: number,
  carriedOver: boolean
): ShiftBar {
  return {
    key,
    staffId: c.staff_id,
    staff: c.staff,
    dayIndex,
    startMin: Math.max(rawStartMin, 0),
    endMin: Math.min(rawEndMin, WEEK_MIN),
    rawStartMin,
    rawEndMin,
    carriedOver,
    startLabel: c.start,
    endLabel: c.end,
    machinesCount: c.machines_count,
    totalMachines: c.total_group_machines,
    onVacation: !!c.on_vacation,
    lane: 0,
  };
}

/**
 * Turns the API's per-day coverage into bars, overnight shifts included.
 * `prevDays` is the same group's previous week; only its Saturday shifts that
 * run past midnight are used, so early Sunday is not shown as a false gap.
 */
export function buildShiftBars(days: GroupDays | undefined, prevDays?: GroupDays): ShiftBar[] {
  const bars: ShiftBar[] = [];
  (prevDays?.Sat?.coverage || []).forEach((c, idx) => {
    const s = parseHHMM(c.start);
    const e = parseHHMM(c.end);
    if (s === null || e === null || e > s || e === 0) return;
    bars.push(toBar(c, `prev-Sat-${idx}-${c.staff_id}`, 6, s - DAY_MIN, e, true));
  });
  DAY_LABELS.forEach((day, dayIndex) => {
    const coverage = days?.[day]?.coverage || [];
    coverage.forEach((c, idx) => {
      const s = parseHHMM(c.start);
      const e = parseHHMM(c.end);
      if (s === null || e === null) return;
      const startMin = dayIndex * DAY_MIN + s;
      let rawEndMin = dayIndex * DAY_MIN + e;
      if (rawEndMin <= startMin) rawEndMin += DAY_MIN;
      bars.push(toBar(c, `${day}-${idx}-${c.staff_id}`, dayIndex, startMin, rawEndMin, false));
    });
  });
  bars.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
  return bars;
}

/** Assigns each bar the first lane where it does not overlap another bar. */
export function packLanes(bars: ShiftBar[]): { bars: ShiftBar[]; laneCount: number } {
  const laneEnds: number[] = [];
  const packed = bars.map((bar) => {
    let lane = laneEnds.findIndex((end) => end <= bar.startMin);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(bar.endMin);
    } else {
      laneEnds[lane] = bar.endMin;
    }
    return { ...bar, lane };
  });
  return { bars: packed, laneCount: Math.max(laneEnds.length, 1) };
}

/**
 * Splits the week into covered, partly covered and unattended time, and finds
 * where two or more people overlap. Vacation shifts are drawn but do not count.
 */
export function computeSegments(
  bars: ShiftBar[],
  totalMachines: number
): { gaps: GapSegment[]; overlaps: OverlapSegment[] } {
  const active = bars.filter((b) => !b.onVacation);
  const points = new Set<number>([0, WEEK_MIN]);
  active.forEach((b) => {
    points.add(b.startMin);
    points.add(b.endMin);
  });
  const sorted = Array.from(points).sort((a, b) => a - b);

  const gaps: GapSegment[] = [];
  const overlaps: OverlapSegment[] = [];

  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (b <= a) continue;
    const on = active.filter((bar) => bar.startMin <= a && bar.endMin >= b);
    const count = on.length;
    const summed = on.reduce((sum, bar) => sum + bar.machinesCount, 0);
    const coveredMachines = totalMachines > 0 ? Math.min(summed, totalMachines) : summed;

    let kind: GapSegment["kind"] | null = null;
    if (count === 0) kind = "none";
    else if (totalMachines > 0 && coveredMachines < totalMachines) kind = "partial";

    if (kind) {
      const last = gaps[gaps.length - 1];
      if (
        last &&
        last.endMin === a &&
        last.kind === kind &&
        last.coveredMachines === coveredMachines
      ) {
        last.endMin = b;
      } else {
        gaps.push({ startMin: a, endMin: b, kind, coveredMachines });
      }
    }

    if (count >= 2) {
      const last = overlaps[overlaps.length - 1];
      if (last && last.endMin === a && last.count === count) {
        last.endMin = b;
      } else {
        overlaps.push({ startMin: a, endMin: b, count });
      }
    }
  }

  return { gaps, overlaps };
}

export function formatWeekMinute(min: number): string {
  const clamped = Math.max(0, Math.min(min, WEEK_MIN));
  const dayIndex = Math.min(Math.floor(clamped / DAY_MIN), 6);
  const inDay = clamped - dayIndex * DAY_MIN;
  const h = Math.floor(inDay / 60);
  const m = inDay % 60;
  return `${DAY_LABELS[dayIndex]} ${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function formatHours(minutes: number): string {
  const hours = minutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`;
}

export const STAFF_PALETTE = [
  "#1677ff", "#52c41a", "#fa8c16", "#722ed1", "#13c2c2", "#eb2f96",
  "#2f54eb", "#a0d911", "#faad14", "#08979c", "#9254de", "#d4380d",
  "#0050b3", "#5b8c00", "#ad6800", "#531dab", "#006d75", "#c41d7f",
  "#874d00", "#391085",
];

/**
 * One colour per operator, the same in every group. Operators are taken in
 * name order and each gets the first palette colour not already used by
 * someone they share a group with; only when every colour is taken by a
 * group-mate does a colour repeat, and then the least-used one is picked.
 */
export function assignStaffColors(
  groups: { staffId: number; staff: string }[][],
  palette: string[] = STAFF_PALETTE
): Map<number, { name: string; color: string }> {
  const names = new Map<number, string>();
  const mates = new Map<number, Set<number>>();
  groups.forEach((members) => {
    const ids = Array.from(new Set(members.map((m) => m.staffId)));
    members.forEach((m) => names.set(m.staffId, m.staff));
    ids.forEach((id) => {
      const set = mates.get(id) ?? new Set<number>();
      ids.forEach((other) => other !== id && set.add(other));
      mates.set(id, set);
    });
  });

  const sorted = Array.from(names.entries()).sort(
    (a, b) => a[1].localeCompare(b[1]) || a[0] - b[0]
  );
  const result = new Map<number, { name: string; color: string }>();
  const uses = new Map<string, number>();
  sorted.forEach(([id, name]) => {
    const taken = new Set<string>();
    mates.get(id)?.forEach((mate) => {
      const c = result.get(mate)?.color;
      if (c) taken.add(c);
    });
    let color = palette.find((c) => !taken.has(c));
    if (!color) {
      color = palette.reduce((best, c) => ((uses.get(c) ?? 0) < (uses.get(best) ?? 0) ? c : best));
    }
    uses.set(color, (uses.get(color) ?? 0) + 1);
    result.set(id, { name, color });
  });
  return result;
}

export function initials(name: string): string {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0].toUpperCase())
    .slice(0, 3)
    .join("");
}
